import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { NAP, BOOKING_URL } from "../lib/config";
import SEOHead from "../lib/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const MAPS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  `${NAP.name}, ${NAP.addressLine1}, ${NAP.city}, ${NAP.state} ${NAP.postal}`
)}`;

export default function ContactPage() {
  return (
    <>
      <SEOHead
        title="Contact & Directions — Call (660) 885-2206"
        description="Contact Westbridge Inn & Suites in Clinton, Missouri. Tap-to-call (660) 885-2206, email info@westbridgeinnandsuites.com, 24-hour front desk, and driving directions."
        canonicalPath="/contact"
      />
      <section className="relative bg-[#1A1A1A] text-white" data-hero-dark="true" data-testid="contact-hero">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="/images/lobby-1.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        </div>
        <div className="relative wb-container py-20 md:py-28">
          <div className="wb-eyebrow text-[#FF9666]">Contact & Directions</div>
          <h1 className="wb-h1 text-white mt-4 max-w-3xl">
            The front desk answers, 24 hours a day.
          </h1>
          <p className="wb-body text-white/85 mt-5 max-w-2xl">
            Call, text, email, or drive over — whichever works. There's no online
            contact form. Talking to a real person is faster, and it's how we like it.
          </p>
        </div>
      </section>
      <Breadcrumbs items={[{ label: "Contact" }]} />

      {/* Primary contact grid */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Phone hero card */}
            <article
              className="lg:col-span-7 wb-card bg-white p-8 md:p-12"
              data-testid="contact-phone-card"
            >
              <div className="wb-eyebrow">Call the front desk</div>
              <div className="wb-divider mt-4" />
              <h2
                className="mt-6 text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#1A1A1A]"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                <a
                  href={`tel:${NAP.phoneTel}`}
                  className="hover:text-[#B94A15] focus-visible:text-[#B94A15]"
                  aria-label={`Call Westbridge Inn and Suites at ${NAP.phone}`}
                  data-testid="contact-phone-link"
                >
                  {NAP.phone}
                </a>
              </h2>
              <p className="wb-body mt-6 max-w-lg">
                Booking questions, group blocks, boat charging, trucker or extended-stay
                rates, pet fees, or anything about the property — the phone rings straight
                to a real person on-site. Any hour, any day.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={`tel:${NAP.phoneTel}`}
                  className="wb-btn-primary"
                  data-testid="contact-tap-to-call"
                >
                  <Phone className="h-4 w-4" /> Tap to call
                </a>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wb-btn-outline"
                  data-testid="contact-book-now"
                >
                  Book Now <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>

            {/* Email + hours */}
            <aside className="lg:col-span-5 space-y-4">
              <article className="wb-card bg-white p-6 md:p-8" data-testid="contact-email-card">
                <div className="wb-eyebrow">Email</div>
                <p className="wb-body mt-3">
                  For quotes on multi-week stays, tournament group blocks, reunions, or
                  anything that's easier in writing.
                </p>
                <a
                  href={`mailto:${NAP.email}?subject=${encodeURIComponent(
                    "Westbridge Inn & Suites — Question"
                  )}`}
                  className="mt-5 inline-flex items-center gap-2 text-[#B94A15] hover:text-[#8F390F] font-bold text-lg break-all"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                  aria-label={`Email Westbridge Inn and Suites at ${NAP.email}`}
                  data-testid="contact-email-link"
                >
                  <Mail className="h-5 w-5 flex-shrink-0" />
                  <span>{NAP.email}</span>
                </a>
                <a
                  href={`mailto:${NAP.email}?subject=${encodeURIComponent(
                    "Westbridge Inn & Suites — Question"
                  )}`}
                  className="wb-btn-primary mt-6 w-full"
                  data-testid="contact-email-button"
                >
                  <Mail className="h-4 w-4" /> Email us
                </a>
              </article>

              <article className="wb-card bg-white p-6 md:p-8" data-testid="contact-hours-card">
                <div className="wb-eyebrow flex items-center gap-2">
                  <Clock className="h-4 w-4" aria-hidden="true" /> Front desk hours
                </div>
                <p
                  className="mt-3 text-2xl font-black text-[#1A1A1A]"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  Staffed 24 hours, 7 days a week.
                </p>
                <p className="wb-body text-sm mt-3">
                  Check-in {NAP.checkIn} · Check-out {NAP.checkOut}. Early check-in and
                  late check-out are subject to availability — call and ask.
                </p>
              </article>
            </aside>
          </div>
        </div>
      </section>

      {/* Address + directions */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
        <div className="wb-container py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="wb-eyebrow">Address</div>
              <div className="wb-divider mt-4" />
              <address
                className="not-italic mt-6"
                data-testid="contact-address"
              >
                <p
                  className="text-2xl font-black text-[#1A1A1A]"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {NAP.name}
                </p>
                <p className="wb-body mt-2">
                  {NAP.addressLine1}
                  <br />
                  {NAP.city}, {NAP.state} {NAP.postal}
                </p>
              </address>
              <p className="wb-body text-sm mt-6 max-w-md">
                We sit at the junction of MO-7, MO-13 and MO-18 — an easy in-and-out from
                Kansas City (1h 15m north), Truman Lake and Warsaw (~38 min south), or
                the Katy Trail's western terminus (2 miles).
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wb-btn-outline"
                  data-testid="contact-directions-link"
                >
                  <MapPin className="h-4 w-4" /> Driving directions
                </a>
              </div>
            </div>
            <div className="lg:col-span-7 wb-card overflow-hidden bg-white" data-testid="contact-map">
              <div className="aspect-[4/3] w-full">
                <iframe
                  title={`Map to ${NAP.name}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    `${NAP.addressLine1}, ${NAP.city}, ${NAP.state} ${NAP.postal}`
                  )}&output=embed`}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-label={`Map showing ${NAP.name} at ${NAP.addressLine1}, ${NAP.city}, ${NAP.state}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#E85D26] text-white">
        <div className="wb-container py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div
              className="text-sm font-black uppercase tracking-[0.18em] text-white/90"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Ready when you are
            </div>
            <h2 className="wb-h2 text-white mt-3 max-w-xl">
              Book direct. Talk to a real person. Sleep well.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#B94A15] font-bold py-3 px-6 rounded-sm hover:bg-[#FDFBF7]"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="contact-final-book"
            >
              Book Now <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`tel:${NAP.phoneTel}`}
              className="inline-flex items-center gap-2 border-2 border-white text-white font-bold py-3 px-6 rounded-sm hover:bg-white hover:text-[#B94A15]"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="contact-final-call"
            >
              <Phone className="h-4 w-4" /> {NAP.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
